import { useCallback, useEffect, useRef, useState } from 'react'
import { apiClient } from '../../lib/api/client'
import type { components } from '../../lib/api/schema.gen'
import { BomNotFoundError } from './types'
import type {
  AddPartsSearchResult,
  BomDetail,
  BomLibraryStatus,
  BomLineItem,
  BomOrderCard,
  BomShareResult,
  BomStatCards,
  LibrarySourceable,
} from './types'
import { problemTitle } from './usePartsLibrary'

/**
 * The data-layer seam for BOM detail (UID-47, screens.md §7). Shaped
 * against the software-engineer's `GET /api/boms/:bomId` handoff — a
 * BFF-backed implementation is a drop-in replacement for
 * `mockBomDetailService` (moved to `./test-fixtures.ts` — code-review
 * UID-106 hygiene finding: test doubles must not be reachable from a
 * runtime module); nothing else in this vertical changes. Mutations
 * (`updateStatus`/`addLines`/`reorderSiblings`/`deleteLine`) mirror the
 * handoff's PATCH/POST/DELETE endpoints; the mock applies them to an
 * in-memory copy of the fixture so the screen sees its own optimistic
 * changes reflected on refetch.
 */
export interface BomDetailService {
  getBomDetail(bomId: string): Promise<BomDetail>
  getOrderHistory(bomId: string): Promise<readonly BomOrderCard[]>
  updateStatus(bomId: string, status: BomLibraryStatus): Promise<void>
  addLines(bomId: string, resultIds: readonly string[]): Promise<void>
  /** Reorders the siblings under `parentLineId` (`null` = top level) to
   * `orderedLineIds` — the mock's simplification of `PATCH
   * /api/boms/:bomId/lines/reorder`, driven by the tree's keyboard-operable
   * Move up/down controls rather than pointer drag (see BomDetailPage.tsx). */
  reorderSiblings(
    bomId: string,
    parentLineId: string | null,
    orderedLineIds: readonly string[],
  ): Promise<void>
  deleteLine(bomId: string, lineId: string): Promise<void>
  searchAddParts(query: string): Promise<readonly AddPartsSearchResult[]>
  /** Adds a single BOM line's sourceable to the cart — the per-row "Add to
   * cart" button's handler AND the multi-select bar's bulk "Add to cart"
   * action call this same method (once per selected line), so there's one
   * code path for both. Backed by `POST /api/boms/:bomId/lines/:lineId/cart`
   * (UID-107): the quantity is server-derived from the stored BOM line, so
   * this carries no client quantity/price. */
  addLineToCart(bomId: string, lineId: string): Promise<void>
  /** UID-295/296 — add ALL of the BOM's sourced leaf parts to the cart in one call
   * (`POST /boms/:bomId/cart`), each at its server-derived BOM quantity. This
   * replaces the earlier per-line fan-out for the "Add all to cart" action — one
   * atomic server round-trip instead of N. OPTIONAL so existing service doubles
   * (test-fixtures / component tests) that predate this method still satisfy the
   * interface; the real `defaultBomDetailService` always provides it. */
  addAllToCart?(bomId: string): Promise<void>
  /** UID-296 — edit a single line's quantity (`PATCH /boms/:bomId/lines/:lineId`).
   * Returns the UPDATED `BomDetail` so the caller can refresh the tree +
   * subtotals/total in one round-trip (the server recomputes downstream).
   * OPTIONAL for the same reason as `addAllToCart` — pre-existing service doubles
   * predate it; `defaultBomDetailService` always provides it. */
  updateLineQuantity?(bomId: string, lineId: string, quantity: number): Promise<BomDetail>
  /** UID-296 — create a "Share a BOM" link (`POST /boms/:bomId/share`). Returns the
   * one-time raw token + shareId + expiry for the UI to show/copy; the server
   * persists only the token's hash, so this value can never be re-fetched. OPTIONAL
   * (see `updateLineQuantity`). */
  shareBom?(bomId: string, expiresInDays?: number): Promise<BomShareResult>
  /** UID-296 — delete a BOM (`DELETE /boms/:bomId`); resolves void on success.
   * OPTIONAL (see `updateLineQuantity`). */
  deleteBom?(bomId: string): Promise<void>
}

type BffBom = components['schemas']['Bom']
type BffBomLineItem = components['schemas']['BomLineItem']

/** Maps a BFF `BomLineItem` node (flat `sourceableId` + `unitPrice`) to this
 * vertical's `BomLineItem` view model (`sourceable` object + `kind`
 * discriminant), recursively. The BFF has no per-line `uom`/`provenance`/DFM
 * detail for a BOM row — only enough to price and label it — so the
 * synthesized `sourceable` carries a placeholder each-based `uom` (mirrors
 * the same placeholder the mock's `addLines` already used) rather than
 * fabricating fields the server never sent. */
function mapBffBomLine(line: BffBomLineItem): BomLineItem {
  const sourceable: LibrarySourceable | undefined = line.isAssembly
    ? undefined
    : {
        id: line.sourceableId ?? line.lineId,
        kind: 'custom',
        uom: {
          saleUom: { code: 'each', kind: 'each' },
          pricingUom: { code: 'each', kind: 'each' },
        },
        name: line.name,
        ...(line.unitPrice ? { indicativePrice: line.unitPrice } : {}),
      }
  return {
    lineId: line.lineId,
    kind: line.isAssembly ? 'assembly' : 'part',
    ...(sourceable ? { sourceable } : {}),
    name: line.name,
    quantity: line.quantity,
    // UID-295/296 — richer per-row display attributes for the redesigned detail
    // table, surfaced straight from the BFF line (null when absent/assembly).
    mpn: line.mpn ?? null,
    category: line.category ?? null,
    leadTime: line.leadTime ?? null,
    datasheetUrl: line.datasheetUrl ?? null,
    thumbnailUrl: line.thumbnailUrl ?? null,
    descriptionLink: line.descriptionLink ?? null,
    ...(line.children.length > 0 ? { children: line.children.map(mapBffBomLine) } : {}),
  }
}

/** Maps the BFF `Bom` aggregate (flat stat fields) to this vertical's
 * `BomDetail` (nested `stats` object) — a pure reshape, not a re-derivation;
 * `computeStats` (moved to `./test-fixtures.ts`) is only for the mock's own
 * optimistic mutations. */
function mapBffBom(bom: BffBom): BomDetail {
  const stats: BomStatCards = {
    totalParts: bom.partCount,
    estimatedCost: bom.estimatedCost ?? null,
    maxLeadTimeDays: bom.maxLeadTimeDays ?? null,
    ...(bom.distinctPartCount !== undefined ? { distinctPartCount: bom.distinctPartCount } : {}),
  }
  return {
    id: bom.id,
    name: bom.name,
    status: bom.status,
    stats,
    lines: bom.tree.map(mapBffBomLine),
    updatedAt: bom.updatedAt,
    description: bom.description ?? null,
  }
}

async function bffGetBomDetail(bomId: string): Promise<BomDetail> {
  const { data, error, response } = await apiClient.GET('/boms/{bomId}', {
    params: { path: { bomId } },
  })
  if (response.status === 404) {
    throw new BomNotFoundError(bomId)
  }
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t load this BOM. Try again.'))
  }
  return mapBffBom(data)
}

/** `searchAddParts` caches its most recent results by the id `AddPartsModal`
 * hands back to `addLines` (screen-lifetime cache — no persistence needed;
 * a fresh search always repopulates it before a fresh add). `addLines`'s
 * frozen signature (`resultIds: readonly string[]`, see `AddPartsModal`)
 * predates this BFF swap and only carries ids, not full candidates, so this
 * is how the REAL `POST /boms/:bomId/lines` call (which needs a `name`) gets
 * one without reshaping that shared modal contract. */
const lastAddPartsSearchResults = new Map<string, AddPartsSearchResult>()

async function bffSearchAddParts(query: string): Promise<readonly AddPartsSearchResult[]> {
  const { data, error } = await apiClient.GET('/boms/add-parts/search', {
    params: { query: { ...(query ? { q: query } : {}) } },
  })
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t search for parts. Try again.'))
  }
  const results: AddPartsSearchResult[] = data.candidates.map((candidate) => ({
    id: candidate.sourceableId ?? candidate.sku ?? candidate.name,
    name: candidate.name,
    source: candidate.source,
    price: candidate.price,
  }))
  lastAddPartsSearchResults.clear()
  for (const result of results) lastAddPartsSearchResults.set(result.id, result)
  return results
}

async function bffAddLines(bomId: string, resultIds: readonly string[]): Promise<void> {
  for (const id of resultIds) {
    const candidate = lastAddPartsSearchResults.get(id)
    const { error } = await apiClient.POST('/boms/{bomId}/lines', {
      params: { path: { bomId } },
      body: {
        name: candidate?.name ?? id,
        quantity: 1,
        ...(candidate?.source === 'library' ? { sourceableId: id } : {}),
      },
    })
    if (error) {
      throw new Error(problemTitle(error, 'We couldn’t add that part to the BOM. Try again.'))
    }
  }
}

async function bffUpdateStatus(bomId: string, status: BomLibraryStatus): Promise<void> {
  const { error } = await apiClient.PATCH('/boms/{bomId}', {
    params: { path: { bomId } },
    body: { status },
  })
  if (error) {
    throw new Error(problemTitle(error, 'We couldn’t update this BOM’s status. Try again.'))
  }
}

/**
 * UID-296 — `PATCH /boms/:bomId/lines/:lineId`. Edits one line's quantity and
 * returns the UPDATED `BomDetail` (the server recomputes downstream
 * subtotals/total), so the caller refreshes the whole tree in one round-trip
 * rather than a separate refetch.
 */
async function bffUpdateLineQuantity(
  bomId: string,
  lineId: string,
  quantity: number,
): Promise<BomDetail> {
  const { data, error } = await apiClient.PATCH('/boms/{bomId}/lines/{lineId}', {
    params: { path: { bomId, lineId } },
    body: { quantity },
  })
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t update this line’s quantity. Try again.'))
  }
  return mapBffBom(data)
}

/**
 * UID-296 — `POST /boms/:bomId/share`. Creates a share link and returns the raw
 * token ONCE (only its hash is persisted server-side, so it can never be
 * re-fetched). `expiresInDays` optionally bounds the link's lifetime.
 */
async function bffShareBom(bomId: string, expiresInDays?: number): Promise<BomShareResult> {
  const { data, error } = await apiClient.POST('/boms/{bomId}/share', {
    params: { path: { bomId } },
    body: expiresInDays !== undefined ? { expiresInDays } : {},
  })
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t create a share link for this BOM. Try again.'))
  }
  return { shareId: data.shareId, token: data.token, expiresAt: data.expiresAt ?? null }
}

/**
 * UID-296 — `DELETE /boms/:bomId`. Deletes the BOM (lines cascade server-side);
 * resolves void on the 204.
 */
async function bffDeleteBom(bomId: string): Promise<void> {
  const { error } = await apiClient.DELETE('/boms/{bomId}', {
    params: { path: { bomId } },
  })
  if (error) {
    throw new Error(problemTitle(error, 'We couldn’t delete this BOM. Try again.'))
  }
}

/**
 * `PATCH /boms/:bomId/lines/reorder` reorders the direct children of
 * `parentLineId` — or the top-level lines when `null` (UID-295/296 closed the
 * earlier top-level-only gap by adding `parentLineId` to the request; the
 * adjacency list already supported reordering under any parent). `orderedLineIds`
 * must be a permutation of exactly that sibling set.
 */
async function bffReorderSiblings(
  bomId: string,
  parentLineId: string | null,
  orderedLineIds: readonly string[],
): Promise<void> {
  const { error } = await apiClient.PATCH('/boms/{bomId}/lines/reorder', {
    params: { path: { bomId } },
    body: { lineIds: [...orderedLineIds], parentLineId },
  })
  if (error) {
    throw new Error(problemTitle(error, 'We couldn’t reorder these lines. Try again.'))
  }
}

/**
 * UID-326 (ADR-0108) — `GET /boms/:bomId/orders`. The BOM's real order history,
 * newest-first, as one `BomOrderCard` per placed order (replaces the earlier
 * flat `BomOrder` line-per-row shape). Normalizes the generated
 * `BomOrderCard`/`BomOrderCardLine` contract types onto this vertical's own
 * view model (per this repo's "generated type → feature view model → narrow
 * component props" convention) — mainly narrowing each line's nullable
 * `Quantity` down to the plain multiplier the card renders. `shipments` is
 * carried through verbatim; the card derives delivered-date/tracking from it
 * client-side rather than the BFF sending separate fields.
 */
async function bffGetOrderHistory(bomId: string): Promise<readonly BomOrderCard[]> {
  const { data, error, response } = await apiClient.GET('/boms/{bomId}/orders', {
    params: { path: { bomId } },
  })
  if (response.status === 404) {
    throw new BomNotFoundError(bomId)
  }
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t load this BOM’s order history. Try again.'))
  }
  return data.orders.map((order) => ({
    orderId: order.orderId,
    poReference: order.poReference ?? null,
    status: order.status,
    orderedOn: order.orderedOn,
    orderedBy: order.orderedBy,
    total: order.total,
    lines: order.lines.map((line) => ({
      name: line.name,
      partNumber: line.partNumber ?? null,
      quantity: line.quantity.value,
      unitPrice: line.unitPrice ?? null,
      lineSubtotal: line.lineSubtotal,
      thumbnailUrl: line.thumbnailUrl ?? null,
    })),
    shipments: order.shipments,
  }))
}

/**
 * UID-295/296 — `POST /boms/:bomId/cart`. Adds ALL of the BOM's sourced leaf parts
 * to the cart in one atomic server call. Mirrors `bffAddLineToCart`: no request
 * body (quantities are server-derived), throws `problemTitle(error, …)` on failure.
 */
async function bffAddAllToCart(bomId: string): Promise<void> {
  const { error } = await apiClient.POST('/boms/{bomId}/cart', {
    params: { path: { bomId } },
  })
  if (error) {
    throw new Error(problemTitle(error, 'We couldn’t add this BOM to your cart. Try again.'))
  }
}

async function bffDeleteLine(bomId: string, lineId: string): Promise<void> {
  const { error } = await apiClient.DELETE('/boms/{bomId}/lines/{lineId}', {
    params: { path: { bomId, lineId } },
  })
  if (error) {
    throw new Error(problemTitle(error, 'We couldn’t remove this line. Try again.'))
  }
}

/**
 * UID-107 — `POST /api/boms/:bomId/lines/:lineId/cart`. Adds the line's sourced
 * part to the caller's cart (or bumps its quantity — one line per Sourceable). The
 * quantity is server-derived from the stored BOM line, so there is NO request body.
 * Mirrors `bffAddLines`/`bffDeleteLine`: throws `problemTitle(error, …)` on failure,
 * which `BomDetailPage`'s existing catch turns into the neutral "Could not add to
 * cart." toast.
 */
async function bffAddLineToCart(bomId: string, lineId: string): Promise<void> {
  const { error } = await apiClient.POST('/boms/{bomId}/lines/{lineId}/cart', {
    params: { path: { bomId, lineId } },
  })
  if (error) {
    throw new Error(problemTitle(error, 'We couldn’t add this line to your cart. Try again.'))
  }
}

/**
 * REAL BFF-backed BOM detail service. `getOrderHistory` is now wired to the real
 * `GET /boms/:bomId/orders` endpoint (UID-295/296, replacing the earlier
 * honest-empty stub); `addLineToCart` (per row) and `addAllToCart` (whole BOM) both
 * hit the real BOM → cart endpoints.
 */
export const defaultBomDetailService: BomDetailService = {
  getBomDetail: bffGetBomDetail,
  getOrderHistory: bffGetOrderHistory,
  updateStatus: bffUpdateStatus,
  addLines: bffAddLines,
  reorderSiblings: bffReorderSiblings,
  deleteLine: bffDeleteLine,
  searchAddParts: bffSearchAddParts,
  addLineToCart: bffAddLineToCart,
  addAllToCart: bffAddAllToCart,
  updateLineQuantity: bffUpdateLineQuantity,
  shareBom: bffShareBom,
  deleteBom: bffDeleteBom,
}

export type BomDetailState =
  | { readonly status: 'loading' }
  | { readonly status: 'not_found' }
  | { readonly status: 'error'; readonly message: string }
  | {
      readonly status: 'ready'
      readonly bom: BomDetail
      readonly orderHistory: readonly BomOrderCard[]
    }

export interface UseBomDetailResult {
  readonly state: BomDetailState
  readonly refetch: () => void
}

/** Loads a single BOM's detail + order history through a
 * `BomDetailService` (defaults to the real BFF-backed service). Same
 * `not_found`/`error`/`loading`/`ready` shape as `usePartDetail` for
 * consistency across the vertical. */
export function useBomDetail(
  bomId: string,
  service: BomDetailService = defaultBomDetailService,
): UseBomDetailResult {
  const [state, setState] = useState<BomDetailState>({ status: 'loading' })
  const [generation, setGeneration] = useState(0)
  const serviceRef = useRef(service)
  serviceRef.current = service

  // `generation` is a manual re-fetch trigger (bumped by `refetch`) that
  // isn't read in the effect body — it exists purely to force a re-run.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above.
  useEffect(() => {
    let cancelled = false
    setState({ status: 'loading' })

    Promise.all([serviceRef.current.getBomDetail(bomId), serviceRef.current.getOrderHistory(bomId)])
      .then(([bom, orderHistory]) => {
        if (!cancelled) setState({ status: 'ready', bom, orderHistory })
      })
      .catch((error: unknown) => {
        if (cancelled) return
        if (error instanceof BomNotFoundError) {
          setState({ status: 'not_found' })
          return
        }
        setState({
          status: 'error',
          message: error instanceof Error ? error.message : 'We couldn’t load this BOM. Try again.',
        })
      })

    return () => {
      cancelled = true
    }
  }, [bomId, generation])

  const refetch = useCallback(() => setGeneration((g) => g + 1), [])

  return { state, refetch }
}
